/**
 * <cart-drawer-upsell>
 *
 * Search & Discovery "related" recommendations shown inside the cart drawer,
 * based on the cart's products. Two modes (rendered as separate instances):
 *
 *   side  — desktop mini side-drawer, left of the cart (vertical scroll, full
 *           cards). Opens after the cart drawer opens; on any cart change it
 *           closes, refreshes, then reopens.
 *   strip — mobile horizontal-scroll strip (compact row cards).
 *
 * Reacts to the Alpine stores via Alpine.effect: the ui store's cartDrawerOpen
 * (when to show) and the cart store's product-set signature (when to refresh).
 * Only the instance active at the current breakpoint fetches; the other is
 * display:none and short-circuits. Excludes products already in the cart.
 *
 * Both modes scroll natively (CSS overflow) — no carousel library.
 */
if (!customElements.get('cart-drawer-upsell')) {
  class CartDrawerUpsell extends HTMLElement {
    constructor() {
      super();
      this.mode = this.dataset.mode || 'strip';
      this.intent = this.dataset.intent || 'related';
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.sectionId = this.dataset.section || 'upsell-recommendations';
      this.mq = window.matchMedia('(min-width: 1024px)');

      this.isOpen = false;
      this.builtSig = null;
      this.buildToken = 0;
      this._sig = '';
      this._ids = [];
      this._open = false;
      this._timer = null;

      this.list = this.querySelector('.cart-drawer-upsell__list');
      this._onMq = () => this.schedule();
    }

    connectedCallback() {
      if (!this.list) return;
      this.startReactivity();
      this.mq.addEventListener('change', this._onMq);
    }

    disconnectedCallback() {
      if (this._effect && window.Alpine && typeof window.Alpine.release === 'function') {
        window.Alpine.release(this._effect);
      }
      if (this._onCartEvent) document.removeEventListener('cart-update', this._onCartEvent);
      this.mq.removeEventListener('change', this._onMq);
      clearTimeout(this._timer);
    }

    startReactivity() {
      const run = () => {
        const ui = window.Alpine && window.Alpine.store('ui');
        const cart = window.Alpine && window.Alpine.store('cart');
        this._open = !!(ui && ui.cartDrawerOpen);
        const ids =
          cart && cart.isLoaded && Array.isArray(cart.items)
            ? cart.items.map((i) => i.product_id).filter(Boolean)
            : [];
        this._ids = ids;
        this._sig = [...new Set(ids.map(String))].sort().join(',');
        this.schedule();
      };

      if (window.Alpine && typeof window.Alpine.effect === 'function') {
        this._effect = window.Alpine.effect(run);
      } else {
        this._onCartEvent = run;
        document.addEventListener('cart-update', this._onCartEvent);
        run();
      }
    }

    // Active only when the cart drawer is open and this mode matches the breakpoint.
    isActive() {
      if (!this._open) return false;
      return this.mode === 'side' ? this.mq.matches : !this.mq.matches;
    }

    schedule() {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this.sync(), 120);
    }

    async sync() {
      if (!this.isActive()) {
        if (this.mode === 'side') this.setOpen(false);
        return;
      }

      const sig = this._sig;
      const ids = this._ids;

      if (sig === this.builtSig) {
        if (this.mode === 'side' && this.hasCards()) this.setOpen(true);
        return;
      }

      // Cart changed (or first build): close (side), refresh, then reopen.
      if (this.mode === 'side') this.setOpen(false);

      await this.rebuild(ids, sig);
      if (sig !== this._sig) return; // cart changed again; a newer sync will handle it

      this.classList.toggle('has-products', this.hasCards());
      if (!this.hasCards()) return;

      if (this.mode === 'side') this.setOpen(true);
    }

    hasCards() {
      return !!this.list.querySelector('[data-upsell-card]');
    }

    async rebuild(ids, sig) {
      this.builtSig = sig;
      const token = ++this.buildToken;

      this.list.innerHTML = '';
      // Reset horizontal scroll position on the mobile strip.
      this.list.scrollLeft = 0;

      const seeds = [...new Set(ids)];
      if (!seeds.length) return;

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const base = Math.floor(this.limit / seeds.length);
        const extra = this.limit % seeds.length;
        const seedLimits = seeds
          .map((id, i) => ({ id, limit: base + (i < extra ? 1 : 0) }))
          .filter((p) => p.limit > 0);

        const responses = await Promise.all(
          seedLimits.map((p) =>
            fetch(
              `${root}recommendations/products?product_id=${p.id}&limit=${p.limit}` +
                `&intent=${this.intent}&section_id=${this.sectionId}`
            )
          )
        );
        if (token !== this.buildToken) return;

        const exclude = new Set(ids.map(String));
        const fragment = document.createDocumentFragment();

        for (let i = 0; i < responses.length; i++) {
          if (!responses[i].ok) continue;
          const text = await responses[i].text();
          if (token !== this.buildToken) return;

          const holder = document.createElement('div');
          holder.innerHTML = text;
          const cards = holder.querySelectorAll('[data-upsell-card]');
          let added = 0;
          for (const card of cards) {
            if (added >= seedLimits[i].limit) break;
            const pid = card.getAttribute('data-product-id');
            if (pid && exclude.has(pid)) continue;
            if (pid) exclude.add(pid);
            fragment.appendChild(card);
            added++;
          }
        }
        if (token !== this.buildToken) return;

        this.list.appendChild(fragment);
      } catch (e) {
        console.error('Cart drawer upsell failed:', e);
      }
    }

    setOpen(v) {
      if (this.isOpen === v) return;
      this.isOpen = v;
      this.classList.toggle('is-open', v);
      this.setAttribute('aria-hidden', v ? 'false' : 'true');
    }
  }

  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
